import { db } from '../../db/indexeddb'
import {
  getCloudTimeEntries,
  getCloudTimeEntryIds,
  getCloudTimeEntriesByIds,
  getDeletedCloudTimeEntries,
  createCloudTimeEntry,
  updateCloudTimeEntry,
  deleteCloudTimeEntry,
} from '../../supabase/queries'
import { CloudTimeEntrySchema, CloudTimeEntryPayloadSchema } from '../sync-schemas'
import { resolveCloudTaskId, resolveLocalTaskId } from '../sync-resolve'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { TimeEntry } from '../../db/models'

/* ── TIME ENTRIES (Fokuszeit, ADR 0025) ───────────────────── */

export const timeEntryPullDescriptor: PullDescriptor<any> = {
  entityName: 'timeEntry',
  settingsKey: 'lastTimeEntrySyncTime',

  fetchRemote: (userId, since) => getCloudTimeEntries(userId, since),
  fetchRemoteIds: (userId) => getCloudTimeEntryIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudTimeEntriesByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudTimeEntries(userId, since),

  safeParse: (raw) => CloudTimeEntrySchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.timeEntries,
  getAllLocal: () => db.timeEntries.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.timeEntries.filter(e => !e.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote) => {
    const localTaskId = await resolveLocalTaskId(remote.task_id, remote.task_local_id)
    // Die Aufgabe ist noch nicht auf diesem Gerät — der nächste Zyklus holt den
    // Abschnitt nach, bis dahin hat der Task-Pull (der vorher läuft) sie angelegt.
    if (!localTaskId) return null
    return {
      id: crypto.randomUUID(),
      taskId: localTaskId,
      startTime: new Date(remote.started_at),
      endTime: remote.ended_at ? new Date(remote.ended_at) : null,
      userId: remote.user_id,
      cloudId: remote.id,
      deletedAt: null,
      createdAt: new Date(remote.created_at),
      updatedAt: new Date(remote.updated_at),
      lastModified: new Date(remote.updated_at),
      synced: true,
      syncedAt: new Date(),
    }
  },

  buildLocalUpdate: async (remote) => ({
    startTime: new Date(remote.started_at),
    endTime: remote.ended_at ? new Date(remote.ended_at) : null,
    updatedAt: new Date(remote.updated_at),
    ...(remote.deleted_at ? { deletedAt: new Date(remote.deleted_at) } : { deletedAt: null }),
  }),
}

export const timeEntryPushDescriptor: PushDescriptor<TimeEntry> = {
  entityName: 'CloudTimeEntry',
  getLocal: async (id) => (await db.timeEntries.get(id)) ?? null,
  getLocalCloudId: (l) => l.cloudId,
  getLocalId: (l) => l.id,
  table: db.timeEntries,

  /*
    **Ein laufender Abschnitt bleibt liegen** (ADR 0025). Hochzuladen, was noch
    läuft, hieße: Das Web zeigt den Fokus eines Rechners als laufend, der
    vielleicht längst abgestürzt ist — und niemand dort könnte ihn beenden.
    `defer` statt Abbruch: Der Eintrag wartet, bis Pause oder Haken das Ende
    setzen, und geht dann von selbst mit.

    Ein Abschnitt kann außerdem nicht vor seiner Aufgabe in der Cloud stehen —
    darum wartet er auch auf deren Cloud-Kennung.
  */
  canCreate: async (entry) => {
    if (!entry.endTime) return { defer: true }
    const cloudTaskId = await resolveCloudTaskId(entry.taskId)
    return cloudTaskId ? { defer: false } : { defer: true }
  },

  canUpdate: async (entry) => {
    const cloudTaskId = await resolveCloudTaskId(entry.taskId)
    return cloudTaskId ? { defer: false } : { defer: true }
  },

  buildCreatePayload: async (entry) => {
    const cloudTaskId = await resolveCloudTaskId(entry.taskId)
    return CloudTimeEntryPayloadSchema.parse({
      task_id: cloudTaskId,
      task_local_id: entry.taskId,
      started_at: entry.startTime.toISOString(),
      ended_at: entry.endTime ? entry.endTime.toISOString() : null,
      local_id: entry.id,
    })
  },

  buildUpdatePayload: async (entry) => {
    const cloudTaskId = await resolveCloudTaskId(entry.taskId)
    const payload: Record<string, any> = CloudTimeEntryPayloadSchema.parse({
      task_id: cloudTaskId,
      task_local_id: entry.taskId,
      started_at: entry.startTime.toISOString(),
      ended_at: entry.endTime ? entry.endTime.toISOString() : null,
      local_id: entry.id,
    })
    // Das Rückgängig einer Löschung belebt die Zeile wieder — die `null` muss
    // ausdrücklich mit, sonst bleibt sie oben tot (Sync-Checkliste Punkt 17).
    if (!entry.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudTimeEntry(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudTimeEntry(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudTimeEntry(cloudId, userId),
}
